/**
 * micro:bit 놀이터 차시별 놀이 장면 — 1단계 묶음(화면 읽기 ① · 입력 따라하기 ② 만으로 되는 것 · PICNIC-MB-FIELD-001 v0.2 §4 · §6 1단계).
 *
 * 순수 상태(DOM · 시간 없음 — 호출하는 쪽이 지난 시간 ms 와 그때의 보드 모습을 넘긴다). 같은 입력이면 같은 결과다.
 *  - 표정 리모컨 코스(L06): 웃는 얼굴 = 앞으로 · 슬픈 얼굴 = 멈춤 · 놀란 얼굴 = 점프. 장애물은 점프로만 넘는다 → 결승.
 *  - 운동장 트랙(L13): 흔들 때마다(올라가는 순간) 한 걸음 → 50걸음 결승.
 *  - 두근두근 달리기(L05): 화면 밝기가 밝음 ↔ 어두움을 오가면 한 번 '두근' — 3번이면 미션 · 빠를수록 캐릭터가 빨리 달린다.
 *  - 미니 영화관(L03): 서로 다른 그림이 3장 이상 차례로 바뀌고 같은 차례가 다시 돌아오면 '상영 중'.
 * 나머지 차시 장면은 lib/little · lib/games · lib/scroll 이고 lib/lessons 가 한 묶음으로 돌린다.
 * 별 모으기(공통)는 lib/play.ts 그대로다. 놀이터 판정(Campus 미션)은 lib/play 의 미션 셋 — 이 장면들의 목표는 화면에서 확인한다.
 */
import type { MbFrame } from "./live";
import { iconOf, isBlank } from "./live";

export type SceneId =
  | "stars"
  | "greet" | "story" | "harbor" | "gate" | "boardGame"
  | "nameTag" | "gallery" | "movie" | "marquee" | "heartbeat" | "faceCourse" | "goals" | "rps" | "reaction" | "quiz" | "dice" | "ballMaze" | "track" | "level"
  | "plaza" | "aiCards"
  | "firefly" | "nightWalk" | "fridge" | "seasons" | "thermoRooms" | "magicForest" | "clapLamp" | "library" | "compassField"
  | "maze" | "balance" | "marathon" | "door" | "watch"
  | "cheer" | "showcase" | "melody" | "band" | "hotCold"
  | "bungee" | "treasureIsland" | "magnetRock" | "sunDial" | "quizShow" | "classroom" | "morningRoom" | "dayLogger";

/**
 * 장면 목록 — 차시(교안 번호) 차례 · i18n 열쇠 `student.microbit.scene.<id>.*`.
 * 저학년 LE01~05 · 입문 · 표준 L01~L14(소리가 필요한 LE06 · L23 은 통로 확장 뒤 — §6.3). 상태 규칙: 이 파일(L03 · L05 · L06 · L13) ·
 * lib/little(LE01~04) · lib/games(L07~L12 · L14 · LE05 주사위) · lib/scroll(L01 · L02 · L04).
 */
export const SCENES: readonly { id: SceneId; lesson: string | null }[] = [
  { id: "stars", lesson: null },
  { id: "greet", lesson: "LE01" },
  { id: "story", lesson: "LE02" },
  { id: "harbor", lesson: "LE03" },
  { id: "gate", lesson: "LE04" },
  { id: "boardGame", lesson: "LE05" },
  { id: "nameTag", lesson: "L01" },
  { id: "gallery", lesson: "L02" },
  { id: "movie", lesson: "L03" },
  { id: "marquee", lesson: "L04" },
  { id: "heartbeat", lesson: "L05" },
  { id: "faceCourse", lesson: "L06" },
  { id: "goals", lesson: "L07" },
  { id: "rps", lesson: "L08" },
  { id: "reaction", lesson: "L09" },
  { id: "quiz", lesson: "L10" },
  { id: "dice", lesson: "L11" },
  { id: "ballMaze", lesson: "L12" },
  { id: "track", lesson: "L13" },
  { id: "level", lesson: "L14" },
  // 마지막 묶음(lib/stages — 무대 넷(음) · 라디오 하나)
  { id: "cheer", lesson: "LE06" },
  { id: "showcase", lesson: "LE12" },
  { id: "melody", lesson: "L23" },
  { id: "band", lesson: "L26" },
  { id: "hotCold", lesson: "X09" },
  // 리모컨 묶음(lib/remotes — 보드가 놀이터 캐릭터를 조종한다)
  { id: "watch", lesson: "L33" },
  { id: "maze", lesson: "L34" },
  { id: "marathon", lesson: "X01" },
  { id: "balance", lesson: "X02" },
  { id: "door", lesson: "X08" },
  { id: "plaza", lesson: null },
  { id: "aiCards", lesson: null },
  // 센서 월드(5단계 — lib/sensorWorld · 안내형: 아이가 시뮬레이터 슬라이더를 움직인다)
  { id: "magicForest", lesson: "LE07" },
  { id: "firefly", lesson: "LE08" },
  { id: "seasons", lesson: "LE09" },
  { id: "bungee", lesson: "L15" },
  { id: "compassField", lesson: "L16" },
  { id: "treasureIsland", lesson: "L17" },
  { id: "magnetRock", lesson: "L18" },
  { id: "nightWalk", lesson: "L19" },
  { id: "thermoRooms", lesson: "L20" },
  { id: "fridge", lesson: "L21" },
  { id: "sunDial", lesson: "L22" },
  { id: "clapLamp", lesson: "L24" },
  { id: "library", lesson: "L25" },
  { id: "quizShow", lesson: "L35" },
  { id: "classroom", lesson: "X04" },
  { id: "morningRoom", lesson: "X05" },
  { id: "dayLogger", lesson: "X10" },
];

/** AI 카드 놀이(3단계 — 통로 ⑤)로 여는 차시 — AI01~AI36(카메라 AI 상품). 차시마다 꾸민 장면은 다음 묶음 */
export const AI_LESSONS: readonly string[] = Array.from({ length: 36 }, (_, i) => `AI${String(i + 1).padStart(2, "0")}`);

/**
 * 라디오 광장(2단계 — 통로 ③)으로 여는 차시 — 짝 · 모둠 · 반 전체가 라디오로 주고받는 차시들(§4 유형 P · R ③).
 * 차시마다 따로 꾸민 놀이는 다음 묶음이고, 지금은 모두 광장(보내고 받은 것 · 같은 그룹 친구)으로 연다.
 */
export const RADIO_LESSONS: readonly string[] = ["LE10", "LE11", "L27", "L28", "L29", "L30", "L31", "L32", "L36", "X03", "X06", "X07"];
/** 라디오 광장 차시 코드(주소 `?play=L27` · 작품 차시 `MB-L27`) — 아니면 null. 광장에서 주고받기 3번이면 이 차시 장면을 해냈다(오늘의 미션 scene:L27 · 2026-10-06) */
export function radioLessonOf(q: string | null | undefined): string | null {
  const t = (q ?? "").trim().replace(/^MB-/i, "").toUpperCase();
  return RADIO_LESSONS.includes(t) ? t : null;
}

/** 주소(`?play=L06` · `?play=track`) · 작품 차시(Bench lesson_meta `MB-L06`)로 장면 고르기 — 모르면 null */
export function sceneOf(q: string | null | undefined): SceneId | null {
  if (!q) return null;
  const t = q.trim().replace(/^MB-/i, "");
  const s = SCENES.find((x) => x.id === t || (x.lesson !== null && x.lesson.toLowerCase() === t.toLowerCase()));
  if (s) return s.id;
  if (AI_LESSONS.some((l) => l.toLowerCase() === t.toLowerCase())) return "aiCards";
  return RADIO_LESSONS.some((l) => l.toLowerCase() === t.toLowerCase()) ? "plaza" : null;
}

// ── 표정 리모컨 코스(L06) ─────────────────────────────────────────────

export const COURSE_LEN = 20;
/** 장애물 칸(이 칸으로 들어가려면 점프 중이어야 한다) */
export const OBSTACLES: readonly number[] = [5, 10, 15];
export const COURSE_STEP_MS = 400;
export const JUMP_MS = 1200;

export interface FaceCourse {
  readonly pos: number;
  readonly wait: number;
  /** 남은 점프 시간(ms) — 0 이면 땅 */
  readonly air: number;
  /** 지금 누르는 표정(마지막 아이콘) */
  readonly mode: "go" | "stop" | "idle";
  /** 장애물 앞에서 막힌 횟수 */
  readonly bumps: number;
  readonly done: boolean;
  readonly wasSurprised: boolean;
}

export const initialFaceCourse = (): FaceCourse => ({ pos: 0, wait: 0, air: 0, mode: "idle", bumps: 0, done: false, wasSurprised: false });

export function stepFaceCourse(s: FaceCourse, dtMs: number, frame: MbFrame | null): FaceCourse {
  if (s.done) return s;
  const icon = frame ? iconOf(frame.leds) : null;
  const surprised = icon === "Surprised";
  let { pos, wait, air, mode, bumps } = s;
  // 놀람이 새로 뜨는 순간 점프(땅에 있을 때만)
  if (surprised && !s.wasSurprised && air <= 0) air = JUMP_MS;
  else air = Math.max(0, air - dtMs);
  if (icon === "Happy") mode = "go";
  else if (icon === "Sad") mode = "stop";
  else if (!frame) mode = "idle";
  // 점프 중에는 앞으로 나아간다(놀람 = 앞으로 뛰기) · 웃음이면 걷기
  const moving = mode === "go" || air > 0;
  if (moving) {
    wait -= dtMs;
    if (wait <= 0) {
      const next = pos + 1;
      if (OBSTACLES.includes(next) && air <= 0) bumps += 1;   // 장애물 앞 — 점프해야 넘는다
      else pos = next;
      wait += COURSE_STEP_MS;
      if (wait < 0) wait = 0;
    }
  } else wait = 0;
  return { pos: Math.min(pos, COURSE_LEN), wait, air, mode, bumps, done: pos >= COURSE_LEN, wasSurprised: surprised };
}

// ── 운동장 트랙(L13) ─────────────────────────────────────────────────

export const TRACK_GOAL = 50;
/** 별 셋까지 계속 센다(3D 무대 별 둘 · 셋) */
export const TRACK_MAX = 300;

export interface Track {
  readonly steps: number;
  readonly wasShake: boolean;
}

export const initialTrack = (): Track => ({ steps: 0, wasShake: false });

export function stepTrack(s: Track, frame: MbFrame | null): Track {
  const shake = !!frame?.shake;
  return { steps: shake && !s.wasShake ? Math.min(s.steps + 1, TRACK_MAX) : s.steps, wasShake: shake };
}

// ── 두근두근 달리기(L05) ─────────────────────────────────────────────

/** 켜진 칸의 평균 밝기(0~9) — 그림은 그대로이고 밝기만 오르내릴 때(L05 '밝기 바꾸기') */
export const litLevel = (leds: readonly number[]): number => {
  const on = leds.filter((v) => v > 0);
  return on.length ? on.reduce((a, b) => a + b, 0) / on.length : 0;
};
export const BEAT_HIGH = 6;
export const BEAT_LOW = 3;
export const BEATS_GOAL = 3;

export interface Heartbeat {
  readonly beats: number;
  /** 지금 밝음 쪽인가(밝음 → 어두움으로 내려가면 한 번) */
  readonly high: boolean;
  /** 마지막 두근 뒤 지난 시간(ms) — 박동 빠르기 */
  readonly since: number;
  /** 최근 박동 간격(ms · 없으면 null) */
  readonly interval: number | null;
  /** 캐릭터가 달린 거리(칸 — 박동이 빠를수록 빨리) */
  readonly run: number;
}

export const initialHeartbeat = (): Heartbeat => ({ beats: 0, high: false, since: 0, interval: null, run: 0 });

export function stepHeartbeat(s: Heartbeat, dtMs: number, frame: MbFrame | null): Heartbeat {
  const since = s.since + dtMs;
  if (!frame) return { ...s, since };
  // 꺼짐(빈 화면)은 가장 어두움 — 켜진 칸의 밝기로 본다(그림이 같고 화면 밝기만 오르내리는 경우까지 — 확장이 화면 밝기를 곱해 보낸다)
  const lv = isBlank(frame.leds) ? 0 : litLevel(frame.leds);
  let { beats, high, interval } = s;
  let next = since;
  if (!high && lv >= BEAT_HIGH) high = true;
  else if (high && lv <= BEAT_LOW) {
    high = false;
    beats += 1;
    interval = since;
    next = 0;
  }
  // 1초에 한 번 뛰는 박동이면 초당 2칸 · 간격이 길면 느리게 · 3초 넘게 없으면 멈춤
  const speed = interval && next < 3000 ? Math.min(4, 2000 / Math.max(250, interval)) : 0;
  return { beats, high, since: next, interval, run: s.run + (speed * dtMs) / 1000 };
}

// ── 미니 영화관(L03) ─────────────────────────────────────────────────

export const MOVIE_FRAMES_GOAL = 3;

export interface Movie {
  /** 그림이 바뀐 차례(켜짐 비트열 · 빈 화면 빼고 · 최근 24장) */
  readonly seq: readonly string[];
  /** 다시 돌아온 적 있는 반복 길이(그림 수) — 0 이면 아직 */
  readonly loop: number;
}

export const initialMovie = (): Movie => ({ seq: [], loop: 0 });

const bitsOf = (leds: readonly number[]) => leds.map((v) => (v >= 5 ? "1" : "0")).join("");

export function stepMovie(s: Movie, frame: MbFrame | null): Movie {
  if (!frame || isBlank(frame.leds)) return s;
  const b = bitsOf(frame.leds);
  if (s.seq.length && s.seq[s.seq.length - 1] === b) return s;   // 같은 그림 그대로
  const seq = [...s.seq, b].slice(-24);
  // 같은 그림이 앞에 있었고 그 사이 그림이 2장 이상이면 반복 — 길이 = 서로 다른 그림 수
  let loop = s.loop;
  const prev = seq.lastIndexOf(b, seq.length - 2);
  if (prev >= 0 && seq.length - 1 - prev >= 2) loop = Math.max(loop, new Set(seq.slice(prev, seq.length - 1)).size);
  return { seq, loop };
}

/** 영화관 목표 — 3장 이상이 반복해 돈다 */
export const moviePlaying = (s: Movie): boolean => s.loop >= MOVIE_FRAMES_GOAL;
